import { generateNewTokenApi, sendOtpApi, verifyOtpApi } from '@api/auth';
import { getCitiesApi, getLoungesApi } from '@api/lounge';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { EnterPhoneFormInputs, LoginState, VerifyOtpForm } from '@utils/types';
import { getLoungeIdFromToken } from '@utils/helpers';
import Cookies from 'js-cookie';

export const sendOtp = createAsyncThunk(
  'sendOtp',
  async (loginPayload: EnterPhoneFormInputs) => {
    await sendOtpApi({
      phoneNo: parseInt(loginPayload.phoneNumber),
    });
    return loginPayload.phoneNumber;
  },
);

export const verifyOtp = createAsyncThunk(
  'verifyOtp',
  async (otpPayload: VerifyOtpForm) => {
    const token = await verifyOtpApi({
      phoneNo: parseInt(otpPayload.phoneNo),
      otp: parseInt(otpPayload.otp),
      captchaToken: otpPayload?.captchaToken as string,
      ...(otpPayload.loungeId ? { loungeId: otpPayload.loungeId } : {}),
    });

    // Derive loungeId from the signed JWT rather than trusting the URL param.
    // The backend embeds lounge_id only when it received and validated a
    // legitimate loungeId, so this is the authoritative source.
    const loungeId = getLoungeIdFromToken(token);
    return { token, loungeId };
  },
);

export const getCities = createAsyncThunk('getCities', async () => {
  const cities = await getCitiesApi();
  return cities;
});

export const getLounges = createAsyncThunk(
  'getLounges',
  async (city: string) => {
    const lounges = await getLoungesApi(city);
    return lounges;
  },
);

export const generateNewToken = createAsyncThunk(
  'generateNewToken',
  async ({
    loungeId,
    loungeName,
  }: {
    loungeId: string;
    loungeName: string;
  }) => {
    if (!loungeId) {
      throw new Error('loungeId is required to generate token');
    }
    const token = await generateNewTokenApi(loungeId);
    return { token, loungeId, loungeName };
  },
);

const initialState: LoginState = {
  phoneNumber: '',
  token: '',
  error: '',
  cities: [],
  lounges: [],
  loungeId: '',
  loungeName: '',
  selectedCity: '',
};

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    resetStep: (state) => {
      Cookies.remove('ca_token');
      state.phoneNumber = '';
      state.token = '';
      state.cities = [];
      state.lounges = [];
      state.loungeId = '';
      state.loungeName = '';
      state.selectedCity = '';
      state.error = '';
    },
    setLoungeName: (state, action: PayloadAction<string>) => {
      state.loungeName = action.payload;
    },
    setSelectedCity: (state, action: PayloadAction<string>) => {
      state.selectedCity = action.payload;
    },
    /**
     * Sync loungeId from URL to Redux state.
     * Used when other parts of the app need loungeId from Redux for backward compatibility.
     */
    setLoungeId: (state, action: PayloadAction<string>) => {
      state.loungeId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendOtp.fulfilled, (state, action) => {
      if (action.payload) {
        state.phoneNumber = action.payload;
        state.error = '';
      }
    });
    builder.addCase(sendOtp.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to send OTP';
    });
    builder.addCase(verifyOtp.fulfilled, (state, action) => {
      if (action.payload.token) {
        Cookies.set('ca_token', action.payload.token);
        state.token = action.payload.token;
        state.loungeId = action.payload.loungeId || '';
        state.error = '';
      }
    });
    builder.addCase(verifyOtp.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to verify OTP';
    });
    builder.addCase(getCities.fulfilled, (state, action) => {
      state.cities = action.payload;
      state.error = '';
    });
    builder.addCase(getCities.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch cities';
    });
    builder.addCase(getLounges.fulfilled, (state, action) => {
      state.lounges = action.payload;
      state.error = '';
    });
    builder.addCase(getLounges.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to fetch lounges';
    });
    builder.addCase(generateNewToken.fulfilled, (state, action) => {
      if (action.payload.token) {
        Cookies.set('ca_token', action.payload.token);
        state.token = action.payload.token;
        state.loungeId = action.payload.loungeId;
        state.loungeName = action.payload.loungeName;
        state.error = '';
      }
    });
    builder.addCase(generateNewToken.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to generate token';
    });
  },
});

// Action creators are generated for each case reducer function
export const { resetStep, setLoungeName, setSelectedCity, setLoungeId } =
  loginSlice.actions;

export default loginSlice.reducer;
